/**
 * 전자책 **표지·목록 메타** — 단일 정의.
 *
 * 웹(고르기 화면)·관리자(표지 붙이기)·엣지(`GET /api/books`)가 같은 규칙을 써야 한다.
 * 세 곳에 따로 적으면 "관리자에선 표지가 보이는데 아이 화면엔 안 나오는" 상태가 된다.
 *
 * ## 표지 그림은 어디서 오는가 (순서대로)
 * 1. **전용 표지** `book.cover.<책id>` — 관리자가 따로 올린 것. 제목 글자가 들어간 진짜 표지.
 * 2. **`cover: true` 로 표시한 페이지**의 그림(`book.page.<책id>.<쪽id>`) — 자동 폴백.
 * 3. 둘 다 없으면 **그리지 않는다** — 웹이 제목으로 절차적 표지를 만든다.
 *
 * 2번 폴백이 중요하다. 이게 없으면 운영자가 표지를 올리기 전까지 고르기 화면이
 * 빈 상자만 늘어선 화면이 된다 — 아이가 무엇을 고를지 알 수 없다.
 */

/** 고르기 화면 카드 한 장에 필요한 것. `GET /api/books` 응답 원소이기도 하다. */
export interface BookSummary {
  id: string;
  title: string;
  pages: number;
  updatedAt?: string;
  /** 표지로 쓸 페이지 id. 전용 표지가 없을 때 이 페이지 그림을 표지로 쓴다. */
  coverPageId?: string;
  /** 한 줄 소개 — 카드에 제목 아래로 들어간다. 없으면 안 그린다. */
  blurb?: string;
  /**
   * 책장에 놓이는 순서(작을수록 앞). 관리자 [📖 전자책]에서 정한다.
   *
   * 안 정한 책은 **정한 책들 뒤로** 간다 — 새 책을 만들 때마다 앞자리를 빼앗으면
   * 운영자가 정해 둔 차례가 매번 흐트러진다.
   */
  order?: number;
  /**
   * 표지를 카드에 **어떻게 담을지**. 카드는 3:4 인데 표지 그림은 비율이 제각각이다.
   *  - `cover`(기본): 카드를 꽉 채운다. 대신 **넘치는 쪽이 잘린다** — 제목이 잘리는 사고가 났다
   *  - `contain`: 그림 전체를 보여준다. 대신 위아래(또는 좌우)에 여백이 생긴다
   */
  coverFit?: "cover" | "contain";
  /**
   * 꽉 채울 때 **어디를 보여줄지**(0~1). 기본은 한가운데(0.5, 0.5).
   * 제목이 위쪽에 있으면 y 를 0 쪽으로 — 그러면 위가 살고 아래가 잘린다.
   */
  coverPos?: { x: number; y: number };
}

/** 전용 표지 그림의 에셋 키. */
export function bookCoverKey(bookId: string): string {
  return `book.cover.${bookId}`;
}

/** 페이지 그림의 에셋 키(책 id 로 네임스페이스 — 책마다 쪽 id 가 겹쳐도 안 부딪힌다). */
export function bookPageImageKey(bookId: string, pageId: string): string {
  return `book.page.${bookId}.${pageId}`;
}

// 「누구 것인가」 판정은 import 없는 파일에 모아 둔다 — 점검 스크립트가 직접 읽는다
export { bookIdOfAssetKey } from "./assetOwner";


/** 표지로 쓸 페이지 id — `cover:true` 우선, 없으면 첫 페이지. 손상 입력에 throw 안 함. */
export function coverPageIdOf(pages: unknown): string | undefined {
  if (!Array.isArray(pages)) return undefined;
  const ids = pages
    .filter((p): p is Record<string, unknown> => !!p && typeof p === "object")
    .map((p) => ({ id: typeof p.id === "string" && p.id ? p.id : undefined, cover: p.cover === true }))
    .filter((p): p is { id: string; cover: boolean } => !!p.id);
  return ids.find((p) => p.cover)?.id ?? ids[0]?.id;
}

/** 한 줄 소개 상한 — 카드 두 줄에 들어가는 길이. 넘치면 카드가 무너진다. */
export const BLURB_MAX = 60;

/** 한 줄 소개 정규화 — 줄바꿈·제어문자를 접고 길이를 자른다. 비면 undefined. */
export function normalizeBlurb(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  // 제어문자는 붙여넣기로 섞여 들어온다 — 화면에서 안 보이는데 카드 줄을 깨뜨린다.
  // 공백으로 바꿔 두면 아래 접기에서 함께 정리된다(줄바꿈·탭도 같은 취급).
  let out = "";
  for (const ch of v) {
    const c = ch.codePointAt(0)!;
    out += c < 0x20 || c === 0x7f ? " " : ch;
  }
  const s = out.replace(/\s+/g, " ").trim().slice(0, BLURB_MAX);
  return s.length > 0 ? s : undefined;
}

/** 저장된 책 문서 → 목록 카드 메타. 엣지·정적 폴백·관리자가 모두 이걸 쓴다. */
export function bookSummaryFromDoc(id: string, doc: unknown, updatedAt?: string): BookSummary {
  const o = (doc ?? {}) as Record<string, unknown>;
  const pages = Array.isArray(o.pages) ? o.pages : [];
  return {
    id,
    title: (typeof o.title_ko === "string" && o.title_ko.trim()) || "전자책",
    pages: pages.length,
    updatedAt,
    coverPageId: coverPageIdOf(pages),
    blurb: normalizeBlurb(o.blurb_ko),
    ...(typeof o.order === "number" && Number.isFinite(o.order) ? { order: o.order } : {}),
    ...(o.coverFit === "contain" ? { coverFit: "contain" as const } : {}),
    ...(coverPosOf(o.coverPos) ? { coverPos: coverPosOf(o.coverPos)! } : {}),
  };
}

/** 표지 초점 좌표 정리 — 0~1 밖이거나 숫자가 아니면 없는 것으로(기본 한가운데). */
function coverPosOf(v: unknown): { x: number; y: number } | undefined {
  if (!v || typeof v !== "object") return undefined;
  const o = v as { x?: unknown; y?: unknown };
  const n = (t: unknown) => (typeof t === "number" && Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : null);
  const x = n(o.x);
  const y = n(o.y);
  return x === null || y === null ? undefined : { x, y };
}

/**
 * 표지를 카드에 담는 CSS 값 — **아이 화면과 관리자 미리보기가 같은 함수를 쓴다.**
 *
 * 화면마다 따로 계산하면 "관리자에선 제목이 보이는데 아이 화면에선 잘리는" 상태가 된다.
 * 그건 눈으로 대조하기 전엔 모른다(이 저장소가 반복해서 당한 사고다).
 */
export function coverImageStyle(b: Pick<BookSummary, "coverFit" | "coverPos">): {
  objectFit: "cover" | "contain";
  objectPosition: string;
} {
  const p = b.coverPos ?? { x: 0.5, y: 0.5 };
  return {
    objectFit: b.coverFit === "contain" ? "contain" : "cover",
    objectPosition: `${Math.round(p.x * 100)}% ${Math.round(p.y * 100)}%`,
  };
}

/**
 * 📚 **책장 차례** — 운영자가 정한 순서대로 세운다.
 *
 * 순서를 안 정한 책은 **뒤로** 보내되 원래 차례는 유지한다(안정 정렬). 새 책을 만들 때마다
 * 앞자리를 빼앗으면 운영자가 정해 둔 차례가 매번 흐트러진다.
 *
 * 아이 화면(고르기)과 관리자 목록이 **같은 함수**를 쓴다 — 갈리면 관리자에서 올린 차례와
 * 아이가 보는 차례가 달라지고, 그건 눈으로 대조하기 전엔 모른다.
 */
export function sortBooks(list: readonly BookSummary[]): BookSummary[] {
  return list
    .map((b, i) => ({ b, i }))
    .sort((a, z) => {
      const ao = a.b.order;
      const zo = z.b.order;
      if (ao === undefined && zo === undefined) return a.i - z.i;
      if (ao === undefined) return 1; // 안 정한 책은 뒤로
      if (zo === undefined) return -1;
      return ao - zo || a.i - z.i;
    })
    .map((x) => x.b);
}

/**
 * 📖→🛍️ **이 책과 이어지는 굿즈** — 다 읽은 아이에게 보여줄 상품.
 *
 * ## 왜
 * 이 서비스의 목적은 **책에서 굿즈·랠리로 잇는 것**인데, 지금 전자책과 굿즈가 서로를 모른다.
 * 다 읽고 나면 그냥 목록으로 돌아갈 뿐이다. 마지막 쪽에서 "이 이야기 속 첨성대 키링 볼까?"
 * 한 장이면 자연스럽게 이어진다.
 *
 * ## 왜 3개까지인가
 * 마지막 쪽은 **책을 다 읽은 순간**이다. 상품을 잔뜩 늘어놓으면 그림책이 카탈로그가 된다.
 * 세 개면 한 줄에 들어가고, 고르는 부담도 없다.
 *
 * ## 붙이는 사람은 운영자다
 * 어느 책에 어느 굿즈를 붙일지는 **관리자 [📖 전자책]** 에서 고른다. 코드가 정하지 않는다.
 */
export const BOOK_GOODS_MAX = 3;

/** 굿즈 id 규칙은 goods 와 같다(안전문자만) — 라우트에 그대로 들어간다. */
const GOODS_ID_RE = /^[a-z0-9_]{1,40}$/;

/** 책 문서의 굿즈 목록을 다듬는다. 이상한 값·중복·초과는 버린다. 절대 throw 안 함. */
export function normalizeBookGoods(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const raw of v) {
    if (out.length >= BOOK_GOODS_MAX) break;
    const id = typeof raw === "string" ? raw.trim() : "";
    if (!GOODS_ID_RE.test(id) || out.includes(id)) continue;
    out.push(id);
  }
  return out;
}

/**
 * 📚 **책 목록 합치기** — 저장소 목록 위에 게시본을 덮어쓴다.
 *
 * ## 왜 이렇게 (2026-08-21 실제 사고)
 * 예전에는 **게시본이 한 권이라도 있으면 저장소 목록을 통째로 버렸다**(`if (out.length > 0) return out`).
 * 그래서 운영자가 [+ 새 전자책] 으로 **빈 책 한 권을 저장한 순간**, 아이 화면에서
 * 「나무 위 도서관」과 「그레그와 병아리의 경주」가 **둘 다 사라졌다.** 지운 적도 없는데.
 *
 * 에러도 안 나고 관리자에서도 똑같이 사라져서, 무엇이 없어졌는지조차 알기 어렵다.
 * 이 저장소는 그림(에셋 매니페스트)에서 이미 「정적 기본 아래에 게시본을 덮어쓰기」를 쓰고 있다 —
 * 책도 같은 규칙으로 맞춘다.
 *
 * ## 규칙
 * - **저장소 목록이 바탕**이고, 같은 id 의 게시본이 있으면 그쪽이 이긴다(운영자가 고친 것)
 * - 게시본에만 있는 책은 **뒤에 덧붙인다**(새로 만든 책)
 * - 저장소 목록의 순서를 지킨다 — 아이가 늘 보던 자리에 그 책이 있다
 *
 * ## 이 규칙의 한계 (알고 고른 것)
 * 저장소 파일에 든 책은 **관리자에서 지워도 다시 나타난다** — 저장소 파일은 브라우저가 못 지운다.
 * 그건 개발자가 파일을 지워야 한다. 「지운 게 되살아난다」보다 **「지운 적 없는 게 사라진다」가
 * 훨씬 나쁜 사고**라 이쪽을 택했다.
 */
export function mergeBookLists(
  fromRepo: readonly BookSummary[],
  fromPublished: readonly BookSummary[],
): BookSummary[] {
  const byId = new Map<string, BookSummary>();
  for (const b of fromPublished) if (b?.id) byId.set(b.id, b);

  const out: BookSummary[] = [];
  const used = new Set<string>();
  for (const b of fromRepo) {
    if (!b?.id || used.has(b.id)) continue;
    used.add(b.id);
    out.push(byId.get(b.id) ?? b); // 고친 게 있으면 그쪽이 이긴다
  }
  for (const b of fromPublished) {
    if (!b?.id || used.has(b.id)) continue;
    used.add(b.id);
    out.push(b); // 새로 만든 책
  }
  // 운영자가 정한 차례가 있으면 그대로 세운다(안 정했으면 위 순서 그대로)
  return sortBooks(out);
}

/**
 * 📚📚 **같은 책이 두 권 서 있는가** — 이름은 같은데 id 가 다른 것.
 *
 * ## 왜 이걸 세나
 * 책을 들이는 길이 둘이다: 관리자에서 한 쪽씩 올리는 길과 저장소에 넣는 길.
 * 같은 원고를 두 길로 반씩 넣으면 **고르기 화면에 같은 이름이 나란히 선다** — 하나는 10쪽짜리
 * 미완성, 하나는 34쪽 완본. 아이는 어느 쪽을 눌러야 할지 알 수 없고, 운영자는 목록을
 * 위에서부터 훑기 때문에 **아래쪽 한 권을 아예 못 본다.**
 *
 * 실제로 그렇게 됐다(2026-08-23, 《그레그, 경주로 폴짝!》). 에러는 없었다. 목록이 길어졌을 뿐이다.
 *
 * ## 이름을 어떻게 같다고 보나
 * 끝의 느낌표·물음표·마침표와 공백은 **떼고** 본다 — 「폴짝」과 「폴짝!」은 사람에게 같은 책이다.
 * 여기서 엄격하게 굴면 정작 잡아야 할 경우를 놓친다.
 */
export function duplicateBookTitles(list: readonly BookSummary[]): string[] {
  const seen = new Map<string, string>();
  const dup = new Set<string>();
  for (const b of list) {
    const key = String(b?.title ?? "")
      .trim()
      .replace(/[!?.\s]+$/u, "")
      .toLowerCase();
    if (!key) continue;
    if (seen.has(key)) dup.add(seen.get(key)!);
    else seen.set(key, String(b.title));
  }
  return [...dup];
}

/**
 * 📚 **책을 지우기 전에 보여줄 말.**
 *
 * ## 왜 「지울까요?」로는 부족한가
 * 책 문서만 지워지고 **그림·소리는 남는다.** 그건 일부러 그런 것이다 — 서버 삭제는 되돌릴 수
 * 없어서 문서를 캐시해 두고 「다시 게시」로 되살리는데, 그림까지 지우면 되살려도 **빈 책**이 된다.
 *
 * 문제는 **아무도 그걸 모른다**는 것이었다. 지운 뒤 파일은 R2 에 그대로 남고, 화면 어디에도
 * 표시가 안 난다. 실제로 운영 매니페스트에 **31개가 그 상태로 남아 있었다**(2026-08-28 발견:
 * 표지 1·본문 10쪽·읽어주기 20). 어느 책 것인지도 이름으로만 겨우 알아냈다.
 *
 * 그래서 **남는다는 사실과 치우는 곳**을 같이 말한다. 지우는 순간이 그걸 알려 줄 유일한 때다.
 */
export function bookDeletePrompt(label: string, assetCount: number): string {
  return [
    `'${label}' 전자책을 지울까요?`,
    "",
    assetCount > 0
      ? `올린 그림·소리 ${assetCount}개는 **지워지지 않고 남아요.**`
      : "이 책에 올린 그림·소리는 없어요.",
    assetCount > 0 ? "책을 다시 만들면 그대로 쓸 수 있어요." : "",
    assetCount > 0 ? "정리하려면 [🚀 출시 준비] 화면에서 볼 수 있어요." : "",
    "",
    "책은 [되돌리기]로 되살릴 수 있어요(이 화면을 떠나기 전까지).",
  ]
    .filter((l, i, a) => !(l === "" && a[i - 1] === "")) // 빈 줄이 겹치지 않게
    .join("\n");
}
